import { Injectable } from '@angular/core';
import { ToggleEventNameType, ToggleEventService } from './toggle-event.service';
import { App } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { MapService } from './map.service';


export type CloseableWindowDuringBackEventType = {
  //the name of component that will be listening to a close command
  //for dialogs, the name is less important, because they close automtically
  windowName: string;
  //angular dialogs close automatically during a back event
  isAutoCloseableWindow?: boolean;
  //closed by the user manually without back event
  canceledCloseableWindow?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class BackEventService {

  /**
   * if property _minimizeAppAfterLastClosedWindow is false and no extra windows are opened/registered above the main map, then the next back event will terminate the app (exit);
   * if true, the app will only minimize
   */
  private _minimizeAppAfterLastClosedWindow: boolean;
  private _isActive: boolean = false;
  private _handlingBlockedBackEvent: boolean = false;
  private _registeredOpenWindowsList: Array<CloseableWindowDuringBackEventType> = [];
  private _androidBackButtonHandler: PluginListenerHandle = null;

  constructor(
    private _toggleEventService: ToggleEventService,
    private _mapService: MapService
  ) {
    this._minimizeAppAfterLastClosedWindow = true;
  }

  set minimizeAppAfterLastClosedWindow(value: boolean) {
    this._minimizeAppAfterLastClosedWindow = value;
  }
  get minimizeAppAfterLastClosedWindow() {
    return this._minimizeAppAfterLastClosedWindow;
  }

  /**
   * don't forget to call removeBackEventListener in your main component ondestroy
   */
  async initializeBackEventListener() {
    console.log('initializing back event listener');
    this._isActive = true;
    this._mapService.backEventServiceActive = true;
    this._handlingBlockedBackEvent = false;

    //we already add something the the history list, so that we can always cancel a back event if necessary, without navigating away from the viewer
    //problem: until the user interacts with the window, every pushState item is disabled and until then the back event doesn't trigger a popstate event (navigator.userActivation.hasBeenActive)
    window.history.pushState({ backEventService: 'initializeBackEventListener_1' }, '', window.location.href);
    window.history.pushState({ backEventService: 'initializeBackEventListener_2' }, '', window.location.href);
    //window.history.replaceState({ backEventService: 'initializeBackEventListener' }, '');

    //this will cancel any back event and deal with opened windows or minimizing the app
    //this only starts working after the user interacted with the window
    window.addEventListener('popstate', (event: PopStateEvent) => this.handlePopStateEvent(event));

    //backButton event is only for android
    //this listener disables the default behaviour(=closing app and going back in browser history)
    //this solves the problem mentionned above (no initial user interaction), for ios no solutions yet
    if (Capacitor.getPlatform() === 'android') {
      this._androidBackButtonHandler = await App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
          if (!this._handlingBlockedBackEvent) {
            //this will trigger a popstate event (see listener above)
            window.history.back();
            //we could continue without this listerer now
            //this.removeAndroidBackButtonHandler();
          }
        } else {
          if (this._minimizeAppAfterLastClosedWindow) {
            console.log('handling android backButton event, minimizing app');
            //App.minimizeApp only supported for android for now
            App.minimizeApp();
          } else {
            console.log('handling android backButton event, exit app');
            //App.exitApp only supported for android for now
            App.exitApp();
          }
        }
      });
    }
  }

  async removeBackEventListener() {
    console.log('removing back event listener');
    this._isActive = false;
    this._mapService.backEventServiceActive = false;
    this._handlingBlockedBackEvent = false;

    window.removeEventListener('popstate', (event: PopStateEvent) => this.handlePopStateEvent(event));
    this.removeAndroidBackButtonHandler();
  }
  async removeAndroidBackButtonHandler() {
    this._androidBackButtonHandler?.remove().finally(() => {
      this._androidBackButtonHandler = null;
    });
  }

  get isActive(){
    return this._isActive;
  }

  handlePopStateEvent(event: PopStateEvent) {
    if (this._isActive) {

      //console.log('history page state ' + JSON.stringify(event && event.state ? event.state : null))

      if (this._handlingBlockedBackEvent) {
        //console.log('ignoring popstate event, normally triggered by our automatic forward event after a manual back event');
        return;
      }

      //first cancel back event by going forward again
      //this will create an extra popstate event 
      //window.history.pushstate would not trigger another popstate event, but it's not a good solution to stay on the same url, e.g. the trace id disappears from the url by the back event, but the route stays on the map
      this._handlingBlockedBackEvent = true;
      window.history.forward();

      /*
      sometimes a swipe event triggers the long tap event, though it should be blocked by the screen border check
      */
      setTimeout(() => {
        if (this._mapService.hasActiveContextMenuTapHoldTimer()) {
          //console.log("cancelled long tap event via BackEventService handlePopStateEvent");
          this._mapService.handleTapHoldEnd();
        }
      }, 300);

      //first we look if we should close opened windows
      //if not, then we close/minimize the app on a android platform
      //in the web version, we always stay on the page
      if (this.hasRegisteredUncanceledOpenWindows()) {
        console.log('handling popstate event, closing registered windows');
        this.closeRegisteredOpenWindows();
      } else {
        console.log('handling popstate event, no registered open windows');

        if (Capacitor.getPlatform() === 'android') {
          //normally this doesn't happen, already catched by the 'backButton' listener above, unless we called removeAndroidBackButtonHandler
          if (this._minimizeAppAfterLastClosedWindow) {
            console.log('handling popstate event for android, minimizing app');
            //App.minimizeApp only supported for android for now
            App.minimizeApp();
          } else {
            console.log('handling popstate event for android, exit app');
            //App.exitApp only supported for android for now
            App.exitApp();
          }

        } else {
          console.log(`handling popstate event for ${Capacitor.getPlatform()}, no extra actions taken`);
        }
      }

      window.setTimeout(() => {
        this._handlingBlockedBackEvent = false;
      }, 300);
    }
  }

  async registerOpenedWindow(newlyOpenedWindow: CloseableWindowDuringBackEventType) {
    if (this._isActive && newlyOpenedWindow) {
      //console.log(`registering opened window ${newlyOpenedWindow.windowName} , isAutoCloseableWindow ${newlyOpenedWindow.isAutoCloseableWindow}`);

      let alreadyRegistered = false;

      this._registeredOpenWindowsList.every((registeredOpenWindow: CloseableWindowDuringBackEventType) => {
        alreadyRegistered = registeredOpenWindow.windowName === newlyOpenedWindow.windowName && !registeredOpenWindow.canceledCloseableWindow;
        return !alreadyRegistered;
      });

      if (alreadyRegistered) {
        //console.log('window was already registered');
        return;
      }

      if (!this.hasRegisteredUncanceledOpenWindows()) {
        window.history.pushState({ backEventService: 'registerOpenedWindow_' + newlyOpenedWindow.windowName }, '', window.location.href);
        //console.log('adding item to the history list so that we can catch a back event');
      }

      this._registeredOpenWindowsList.push(newlyOpenedWindow);

      //removing old canceled items
      this._registeredOpenWindowsList = this._registeredOpenWindowsList.filter((registeredOpenWindow: CloseableWindowDuringBackEventType) => {
        return !registeredOpenWindow.canceledCloseableWindow;
      });

      //usually only 2-3 windows are or can be open above map, we can assume most of them are already close
      //safe upper limit
      if (this._registeredOpenWindowsList.length > 10) {
        this._registeredOpenWindowsList = this._registeredOpenWindowsList.slice(-10);
      }
    }
  }

  /**
   * in general it's not necessary to unregister a previously registered window
   * 
   * @param closedWindow 
   */
  async unRegisterOpenedWindow(closedWindow: CloseableWindowDuringBackEventType) {
    if (this._isActive && closedWindow) {
      //needs to happen a bit later for autocloseable windows, because they will call this method during a back event and a manual close
      //a native app would otherwise close because method hasRegisteredUncanceledOpenWindows will return false in method handleBackEvent
      window.setTimeout(() => {
        this._registeredOpenWindowsList.forEach((registeredOpenWindow: CloseableWindowDuringBackEventType) => {
          if (registeredOpenWindow.windowName === closedWindow.windowName && !registeredOpenWindow.canceledCloseableWindow) {
            //console.log(`unregistering closed window ${registeredOpenWindow.windowName} , isAutoCloseableWindow ${registeredOpenWindow.isAutoCloseableWindow}`);
            registeredOpenWindow.canceledCloseableWindow = true;
          };
        });
      }, 1000);
    }
  }

  hasRegisteredUncanceledOpenWindows() {
    let count = 0;
    this._registeredOpenWindowsList.forEach((registeredOpenWindow: CloseableWindowDuringBackEventType) => {
      count += registeredOpenWindow.canceledCloseableWindow ? 0 : 1
    });
    return count > 0;
  }

  closeRegisteredOpenWindows() {
    //dialogs close automtically when navigating back
    //others we will close via ToggleEventService
    //console.log(`notifying ${this._registeredOpenWindowsList.length} registered window(s) that they should close, if still open`);
    this._registeredOpenWindowsList.forEach((registeredOpenWindow: CloseableWindowDuringBackEventType) => {
      if (!registeredOpenWindow.canceledCloseableWindow) {
        registeredOpenWindow.canceledCloseableWindow = true;
        if (registeredOpenWindow.isAutoCloseableWindow) {
          //console.log(`nothing to do for window ${registeredOpenWindow.windowName} , isAutoCloseableWindow ${registeredOpenWindow.isAutoCloseableWindow} `);
        } else {
          //console.log(`sending close command for window ${registeredOpenWindow.windowName} , isAutoCloseableWindow ${registeredOpenWindow.isAutoCloseableWindow}`);
          this._toggleEventService.toggleEvent({ eventName: ToggleEventNameType.CLOSE_COMMAND, forwardedFor: registeredOpenWindow.windowName });
        }
      }
    });
  }
}
